'use client';

import { FC, useEffect, useRef } from 'react';
import { Content, isFilled, asText } from '@prismicio/client';
import { SliceComponentProps, PrismicRichText } from '@prismicio/react';
import { PrismicNextImage } from '@prismicio/next';
import HighlightedText from '@/components/HighlightedText/HighlightedText';
import { useIsClient } from '@/hooks';

/**
 * Props for `CriteriaTable`.
 */
export type CriteriaTableProps =
  SliceComponentProps<Content.CriteriaTableSlice>;

/**
 * Component for "CriteriaTable" Slices.
 * A two-column table showing prioritization factors and corresponding business questions.
 */
const CriteriaTable: FC<CriteriaTableProps> = ({ slice }) => {
  const { heading, description } = slice.primary;
  const isKeyTakeaways = slice.variation === 'keyTakeaways';

  // Extract criteria for basic variation
  const criteria = 'criteria' in slice.primary ? slice.primary.criteria : null;

  // Extract takeaways for keyTakeaways variation
  const takeaways =
    'Takeaways' in slice.primary ? slice.primary.Takeaways : null;

  const headerRef = useRef<HTMLDivElement>(null);
  const tableRef = useRef<HTMLDivElement>(null);
  const isClient = useIsClient();

  useEffect(() => {
    if (!isClient) {
      return;
    }

    const observerOptions = {
      threshold: 0.1,
      rootMargin: '0px 0px -10% 0px',
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add('animate-fade-in');
          entry.target.classList.remove('opacity-0', 'translate-y-8');
        }
      });
    }, observerOptions);

    const headerEl = headerRef.current;
    const tableEl = tableRef.current;

    if (headerEl) {
      headerEl.style.transitionDelay = '0ms';
      observer.observe(headerEl);
    }

    if (tableEl) {
      tableEl.style.transitionDelay = '200ms';
      observer.observe(tableEl);
    }

    return () => {
      if (headerEl) {
        observer.unobserve(headerEl);
      }
      if (tableEl) {
        observer.unobserve(tableEl);
      }
    };
  }, [isClient]);

  // Render keyTakeaways variation
  if (isKeyTakeaways) {
    return (
      <section
        data-slice-type={slice.slice_type}
        data-slice-variation={slice.variation}
        className="bg-white py-[80px] px-5 md:px-16"
      >
        <div className="max-w-[1040px] mx-auto flex flex-col gap-10 items-start">
          {/* Header Section */}
          <div
            ref={headerRef}
            className="flex flex-col gap-6 w-full opacity-0 translate-y-8 transition-all duration-700 ease-out"
          >
            {/* Heading */}
            {isFilled.richText(heading) && (
              <h2 className="text-[32px] md:text-[42px] font-medium leading-[1.2] text-[#041018]">
                <HighlightedText
                  text={asText(heading) || ''}
                  highlightClassName="text-[#069183]"
                />
              </h2>
            )}

            {/* Description */}
            {isFilled.richText(description) && (
              <PrismicRichText
                field={description}
                components={{
                  paragraph: ({ children }) => (
                    <p className="text-lg font-normal leading-[1.5] text-[#041018]">
                      {children}
                    </p>
                  ),
                }}
              />
            )}
          </div>

          {/* Key Takeaways Section */}
          <div
            ref={tableRef}
            className="flex flex-col gap-2 w-full opacity-0 translate-y-8 transition-all duration-700 ease-out"
          >
            {/* Section Label */}
            <p className="text-xl md:text-[22px] font-semibold italic leading-[1.5] text-[#041018] text-center w-full">
              Key Takeaways
            </p>

            {/* Takeaway Cards Grid */}
            {takeaways && takeaways.length > 0 && (
              <div className="flex flex-wrap gap-6 items-start justify-center w-full">
                {/* Split into two columns */}
                <div className="flex-1 min-w-[280px] flex flex-col gap-6">
                  {takeaways
                    .filter((_, index) => index % 2 === 0)
                    .map((item, index) => {
                      const originalIndex = index * 2;
                      const isAlternate =
                        originalIndex === 1 || originalIndex === 2;
                      return (
                        <div
                          key={originalIndex}
                          className={`flex flex-col gap-2 p-4 rounded-xl shadow-[0px_4px_12px_0px_rgba(13,10,44,0.06)] ${
                            isAlternate
                              ? 'bg-[#f4fcf7] border border-white'
                              : 'bg-white border border-[#dedee9]'
                          }`}
                        >
                          {/* Icon */}
                          {isFilled.image(item.icon) && (
                            <div className="w-12 h-12 flex-shrink-0">
                              <PrismicNextImage
                                field={item.icon}
                                className="w-full h-full object-contain"
                              />
                            </div>
                          )}

                          {/* Title */}
                          {item.title && (
                            <h3 className="text-lg md:text-xl font-semibold leading-[1.4] text-[#041018]">
                              {item.title}
                            </h3>
                          )}

                          {/* Description */}
                          {item.description && (
                            <p className="text-base md:text-lg font-normal leading-[1.4] text-[#041018]">
                              {item.description}
                            </p>
                          )}
                        </div>
                      );
                    })}
                </div>
                <div className="flex-1 min-w-[280px] flex flex-col gap-6">
                  {takeaways
                    .filter((_, index) => index % 2 === 1)
                    .map((item, index) => {
                      const originalIndex = index * 2 + 1;
                      const isAlternate =
                        originalIndex === 1 || originalIndex === 2;
                      return (
                        <div
                          key={originalIndex}
                          className={`flex flex-col gap-2 p-4 rounded-xl shadow-[0px_4px_12px_0px_rgba(13,10,44,0.06)] ${
                            isAlternate
                              ? 'bg-[#f4fcf7] border border-white'
                              : 'bg-white border border-[#dedee9]'
                          }`}
                        >
                          {/* Icon */}
                          {isFilled.image(item.icon) && (
                            <div className="w-12 h-12 flex-shrink-0">
                              <PrismicNextImage
                                field={item.icon}
                                className="w-full h-full object-contain"
                              />
                            </div>
                          )}

                          {/* Title */}
                          {item.title && (
                            <h3 className="text-lg md:text-xl font-semibold leading-[1.4] text-[#041018]">
                              {item.title}
                            </h3>
                          )}

                          {/* Description */}
                          {item.description && (
                            <p className="text-base md:text-lg font-normal leading-[1.4] text-[#041018]">
                              {item.description}
                            </p>
                          )}
                        </div>
                      );
                    })}
                </div>
              </div>
            )}
          </div>
        </div>
      </section>
    );
  }

  // Default variation (basic)
  return (
    <section
      data-slice-type={slice.slice_type}
      data-slice-variation={slice.variation}
      className="bg-white py-[50px] pt-[94px] px-5 md:px-16"
    >
      <div className="max-w-[1040px] mx-auto flex flex-col gap-[54px] items-center">
        {/* Header Section */}
        <div
          ref={headerRef}
          className="flex flex-col gap-6 items-center text-center w-full opacity-0 translate-y-8 transition-all duration-700 ease-out"
        >
          {/* Heading with highlight support */}
          {isFilled.richText(heading) && (
            <h2 className="text-[32px] md:text-[42px] font-medium leading-[1.2] text-[#041018]">
              <HighlightedText
                text={asText(heading) || ''}
                highlightClassName="text-[#069183]"
              />
            </h2>
          )}

          {/* Description */}
          {isFilled.richText(description) && (
            <PrismicRichText
              field={description}
              components={{
                paragraph: ({ children }) => (
                  <p className="text-lg font-normal leading-[1.4] text-[#041018]">
                    {children}
                  </p>
                ),
                strong: ({ children }) => (
                  <strong className="font-semibold">{children}</strong>
                ),
              }}
            />
          )}
        </div>

        {/* Criteria Table */}
        {criteria && criteria.length > 0 && (
          <div
            ref={tableRef}
            className="flex flex-col gap-4 w-full relative opacity-0 translate-y-8 transition-all duration-700 ease-out"
          >
            {/* Column Headers */}
            <div className="flex gap-4 items-center pl-4">
              <div className="w-[200px] md:w-[251px] shrink-0">
                <span className="inline-block bg-[#002726] text-white text-xs md:text-sm font-medium uppercase px-2 py-1 rounded-lg">
                  Prioritization Factor
                </span>
              </div>
              <div className="flex-1">
                <span className="inline-block bg-[#002726] text-white text-xs md:text-sm font-medium uppercase px-2 py-1 rounded-lg">
                  Your Business Question
                </span>
              </div>
            </div>

            {/* Criteria Rows */}
            {criteria.map((item, index) => (
              <div
                key={index}
                className="bg-[#f4fcf7] border border-white rounded-xl p-4 shadow-[0px_4px_12px_0px_rgba(13,10,44,0.06)] flex flex-col md:flex-row gap-4 md:gap-[15px] items-start md:items-center"
              >
                {/* Factor Badge */}
                <div className="bg-white rounded-lg p-2 w-full md:w-[251px] shrink-0">
                  <span className="text-base md:text-lg font-normal leading-[1.4] text-[#041018]">
                    {item.factor}
                  </span>
                </div>

                {/* Business Question */}
                <div className="flex-1">
                  <p className="text-lg md:text-xl font-semibold leading-[1.4] text-[#041018]">
                    {item.question}
                  </p>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </section>
  );
};

export default CriteriaTable;
